import React from "react";
import {
  Card,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

function getProductDisplayName(product, lang) {
  if (!product) return "";
  const t = product?.title?.[lang] || product?.title?.["sk-SK"] || product?.title?.["en-US"];
  if (typeof t === "string" && t) return t;
  const pn = product?.productName?.[lang] || product?.productName?.["sk-SK"] || product?.productName?.["en-US"];
  if (typeof pn === "string" && pn) return pn;
  if (product?.productName && typeof product.productName === "object") {
    const v = Object.values(product.productName).find((x) => typeof x === "string" && x);
    if (v) return v;
  }
  if (product?.title && typeof product.title === "object") {
    const v = Object.values(product.title).find((x) => typeof x === "string" && x);
    if (v) return v;
  }
  return product?.name || product?.id || "";
}

/** Vyextrahuje URL obrázka z hodnoty (string alebo objekt s url/src). */
function extractImageUrl(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null) {
    const u = value?.url ?? value?.src ?? value?.imageUrl ?? value?.link;
    if (typeof u === "string") return u;
  }
  return "";
}

/** Vráti URL obrázka produktu (rôzne názvy polí v API). */
function getProductImageUrl(product) {
  const candidates = [
    product?.imageUrl,
    product?.image,
    product?.customProductImageUrl,
    product?.mainImage,
    product?.productImage,
    product?.metadata?.image,
    Array.isArray(product?.images) && product?.images[0] ? product.images[0] : null,
  ];
  for (const v of candidates) {
    const url = extractImageUrl(v);
    if (url) return url;
  }
  return "";
}

/** Váha z productParameters.weightKg (kg) na gramy, napr. "300 g". Prázdny string ak nie je. */
function formatWeightGrams(weightKg) {
  const num = typeof weightKg === "number" ? weightKg : parseFloat(weightKg);
  if (!Number.isFinite(num) || num <= 0) return "";
  return `${Math.round(num * 1000)} g`;
}

/** Objem z productParameters.volumeLiters (litre), napr. "0.5 l". Prázdny string ak nie je. */
function formatVolumeLiters(volumeLiters) {
  const num =
    typeof volumeLiters === "number" ? volumeLiters : parseFloat(volumeLiters);
  if (!Number.isFinite(num) || num <= 0) return "";
  return `${parseFloat(num.toFixed(2))} l`;
}

const DsokDailyMenuProductRow = ({
  product,
  number,
  onToggleActive,
  onDelete,
  onUpdate,
  lang = "sk-SK",
}) => {
  const { t } = useTranslation();
  const title = getProductDisplayName(product, lang);
  const price = product?.price ?? "";
  const isActive = (() => {
    const v = product?.isActive ?? product?.active;
    if (v === false || v === "false") return false;
    if (v === true || v === "true") return true;
    return true;
  })();
  // Zobraziť na webe — default true (undefined = true).
  const showOnWeb = product?.showOnWeb === false ? false : true;
  // Názov pre web — default rovnaký ako názov jedla.
  const webTitleValue =
    product?.webTitle && typeof product.webTitle === "object"
      ? product.webTitle[lang] ??
        product.webTitle["sk-SK"] ??
        product.webTitle["en-US"] ??
        title
      : title;
  // Množstvo: váha (g) má prednosť, inak objem (l).
  const weightDisplay =
    formatWeightGrams(product?.weightKg) ||
    formatVolumeLiters(product?.volumeLiters);
  // Čísla alergénov (read-only) — robustne z rôznych tvarov (čísla/reťazce/objekty/JSON).
  const allergensDisplay = (() => {
    let raw = product?.allergens;
    if (typeof raw === "string" && raw.trim()) {
      try {
        raw = JSON.parse(raw);
      } catch (e) {
        raw = raw.split(/[,;\s]+/);
      }
    }
    if (!Array.isArray(raw)) return "";
    const nums = raw
      .map((it) => {
        if (typeof it === "number") return it;
        if (typeof it === "string") return Number(it);
        if (it && typeof it === "object")
          return Number(it.number ?? it.id ?? it.value ?? it.code);
        return NaN;
      })
      .filter(Number.isFinite);
    return [...new Set(nums)].sort((a, b) => a - b).join(", ");
  })();
  const imageUrl = getProductImageUrl(product);
  const productId = product?.id ?? product?._id;
  const openProductInNewTab = () => {
    if (!productId) return;
    window.open(
      `/customer/dsok/products/${productId}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <Card sx={{ mb: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ px: 2, py: 1.5 }}
      >
        <Div sx={{ width: 28, flexShrink: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {number}
          </Typography>
        </Div>
        <Stack sx={{ flexShrink: 0 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={!!isActive}
                onChange={(e) =>
                  onToggleActive && onToggleActive(e.target.checked)
                }
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                {t("pages.dsokDailyMenu.active", { defaultValue: "Aktívne" })}
              </Typography>
            }
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showOnWeb}
                onChange={(e) =>
                  onUpdate && onUpdate("showOnWeb", e.target.checked)
                }
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                {t("pages.dsokDailyMenu.showOnWeb", {
                  defaultValue: "Zobraziť na webe",
                })}
              </Typography>
            }
            sx={{ m: 0 }}
          />
        </Stack>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            width={50}
            height={50}
            style={{ objectFit: "cover", borderRadius: 4 }}
          />
        ) : null}
        <Div sx={{ flex: 1, minWidth: 0 }}>
          {onUpdate ? (
            <Stack spacing={1}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <TextField
                  size="small"
                  fullWidth
                  label={t("pages.presentation.label")}
                  value={title}
                  onChange={(e) =>
                    onUpdate("title", {
                      ...(product.title || {}),
                      [lang]: e.target.value,
                    })
                  }
                />
                {productId ? (
                  <Tooltip
                    title={t("pages.dsokDailyMenu.openProduct", {
                      defaultValue: "Otvoriť produkt v novom tabe",
                    })}
                  >
                    <IconButton
                      size="small"
                      onClick={openProductInNewTab}
                      aria-label={t("pages.dsokDailyMenu.openProduct", {
                        defaultValue: "Otvoriť produkt v novom tabe",
                      })}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Stack>
              <TextField
                size="small"
                fullWidth
                label={t("pages.dsokDailyMenu.webTitle", {
                  defaultValue: "Názov pre web",
                })}
                value={webTitleValue}
                onChange={(e) =>
                  onUpdate("webTitle", {
                    ...(product.webTitle || {}),
                    [lang]: e.target.value,
                  })
                }
              />
            </Stack>
          ) : (
            <Typography variant="body1" noWrap>
              {title}
            </Typography>
          )}
        </Div>
        <Div sx={{ width: 80, flexShrink: 0 }}>
          <TextField
            size="small"
            fullWidth
            label={t("pages.dsokDailyMenu.weight", {
              defaultValue: "Váha/objem",
            })}
            value={weightDisplay || "—"}
            InputProps={{ readOnly: true }}
          />
        </Div>
        <Div sx={{ width: 110, flexShrink: 0 }}>
          <TextField
            size="small"
            fullWidth
            label={t("pages.dsokDailyMenu.allergens", {
              defaultValue: "Alergény",
            })}
            value={allergensDisplay || "—"}
            InputProps={{ readOnly: true }}
            title={t("pages.dsokDailyMenu.allergensHint", {
              defaultValue: "Čísla alergénov (len na čítanie)",
            })}
          />
        </Div>
        <Div sx={{ width: 100 }}>
          {onUpdate ? (
            <TextField
              size="small"
              type="text"
              label={t("pages.products.price")}
              value={price}
              onChange={(e) => onUpdate("price", e.target.value)}
            />
          ) : (
            <Typography variant="body2">{price} €</Typography>
          )}
        </Div>
        <IconButton
          size="small"
          onClick={() => onDelete && onDelete()}
          aria-label={t("buttons.delete")}
        >
          <DeleteForeverIcon />
        </IconButton>
      </Stack>
    </Card>
  );
};

export default DsokDailyMenuProductRow;
